import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";

import { type Customer, type CustomerData } from "./types";

function getIsRegistered(customerSelection: CustomerSelection) {
  if (customerSelection.isRegistered !== undefined) {
    return customerSelection.isRegistered ? "REGISTERED" : "UNREGISTERED";
  }

  if (customerSelection.customerClassification !== undefined) {
    return customerSelection.customerClassification === "REGISTERED"
      ? "REGISTERED"
      : "UNREGISTERED";
  }

  return "UNREGISTERED";
}

function getBCMCardNumber(customerSelection: CustomerSelection) {
  // CP told me to get element [0] :-(
  return customerSelection?.cards?.[0]?.cardNumber;
}

const isCorrectContextType = (
  contact: ContactMethod,
  addressContextType: string
) => {
  return (
    contact.contextType === addressContextType ||
    contact.contextType === "HOME_GRP1"
  );
};

function getCustomerType(customerType: string) {
  const type = customerType.toLocaleLowerCase();
  return ["business", "invoice"].includes(type) ? "Business" : "Individual";
}

export function mapCustomerDataToCustomer(
  market: string,
  language: string,
  customer?: CustomerData
): Customer | undefined {
  const customerData = customer?.customerSelection;

  if (!customerData) {
    return undefined;
  }

  let address;

  const billingAddress = {
    email: customerData.billingAddress.email,
    phoneNumber: !isEmpty(customerData.billingAddress.mobile)
      ? customerData.billingAddress.mobile
      : customerData.billingAddress.phone,
    postalCode: customerData.billingAddress.postalCode,
    streetAddress1: customerData.billingAddress.streetAddress1,
    streetAddress2: customerData.billingAddress.streetAddress2,
    streetAddress3: customerData.billingAddress.streetAddress3,
    city: customerData.billingAddress.city,
    country: customerData.billingAddress.countryCode,
    state: customerData.billingAddress.state,
    contextType: customerData.billingAddress.contextType,
  };

  const deliveryAddress = {
    email: customerData.deliveryAddress.email,
    phoneNumber: !isEmpty(customerData.deliveryAddress.mobile)
      ? customerData.deliveryAddress.mobile
      : customerData.deliveryAddress.phone,
    postalCode: customerData.deliveryAddress.postalCode,
    streetAddress1: customerData.deliveryAddress.streetAddress1,
    streetAddress2: customerData.deliveryAddress.streetAddress2,
    streetAddress3: customerData.deliveryAddress.streetAddress3,
    city: customerData.deliveryAddress.city,
    country: customerData.deliveryAddress.countryCode,
    state: customerData.deliveryAddress.state,
    contextType: customerData.deliveryAddress.contextType,
  };

  // address with only email and phone is considered empty
  const deliveryWithoutContact = omit(deliveryAddress, [
    "email",
    "phoneNumber",
  ]);
  if (isEmpty(omitBy(deliveryWithoutContact, isNil))) {
    address = billingAddress;
  } else {
    address = deliveryAddress;
  }

  const email = customerData.contactMethods?.find(
    (contact) =>
      contact.type === "EMAIL" &&
      isCorrectContextType(contact, address.contextType)
  )?.value;

  const phoneNumber = customerData.contactMethods?.find(
    (contact) =>
      (contact.type === "SMS" || contact.type === "PHONE") &&
      isCorrectContextType(contact, address.contextType)
  )?.value;

  // Take billing address state if delivery address state is missing
  const stateCode = !isEmpty(address.state)
    ? address.state
    : billingAddress.state;

  const mappedData: Customer = {
    firstName: customerData.firstName,
    lastName: customerData.lastName,
    email: customerData.emailID ?? email ?? address.email,
    familyNumber:
      customerData.familyId ||
      customerData.loyaltyCardNbr ||
      getBCMCardNumber(customerData),
    postalCode: address.postalCode,
    phoneNumber: phoneNumber ?? address.phoneNumber,
    city: address.city,
    customerType: getCustomerType(customerData.customerType),
    customerAccountState: getIsRegistered(customerData),
    streetAddress1: address.streetAddress1,
    streetAddress2: address.streetAddress2,
    streetAddress3: address.streetAddress3,
    state: stateCode,
    organizationName: customerData.organizationName || customerData.companyName,
    partyId: customerData.partyUID,
    bcmId: customerData.businessCustomerId,
    country: address.country,
    language:
      customerData.languageCode || customerData.preferredLocale?.slice(0, 2),
    customerLocale:
      customerData.preferredLocale ||
      `${(!isEmpty(customerData.languageCode) ? customerData.languageCode : language).toLowerCase()}-${market.toUpperCase()}`,
    consents: {
      privacyPolicy: false,
      termsAndCondition: false,
    },
  };
  return mappedData;
}
